// full file content
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { freeze } from "immer";

// 🔍 DIAGNOSTIC: Import diagnostic system
import vb from "../../diag/vbDiag";

// Timeline comments
import type { TimelineComment } from "../types/comments";
import {
  FxBlockSchema,
  normalizeFxBlocks,
  sortFxBlocksByTrackOrder,
  type FxBlock,
} from "./fxTypes";
import { clipCacheStatus, stableThumbCache } from "./TimelineThumbs";

// ── Types, constants, helpers from EditorStore.types ──
import {
  clamp,
  id,
  normalizeEffectRegion,
  MAX_HISTORY,
  MAX_EFFECT_REGIONS,
  FX_BLOCK_DEFAULT_CREATE_WINDOW_SEC,
  FX_BLOCK_DEFAULT_CREATE_WINDOW_EPSILON_SEC,
  VIDEO_TRACK_ID,
  CAMERA_TRACK_ID,
  AUDIO_TRACK_ID,
  OVERLAY_TRACK_ID,
  TEXT_TRACK_ID,
  EFFECTS_TRACK_ID,
  TRANSITIONS_TRACK_ID,
  PLACEHOLDER_ASSET_ID,
  MISSING_ASSET_PLACEHOLDER,
} from "./EditorStore.types";
import type {
  Asset,
  Track,
  ColorAdjustments,
  AudioAdjustments,
  Clip,
  Marker,
  Keyframe,
  Transition,
  EffectRegion,
  HistoryEntry,
  TimelineState,
  FxBlockTargetClipWindow,
} from "./EditorStore.types";

// ── Re-exports: every symbol previously importable from ./EditorStore ──
export type { Asset, Track, ColorAdjustments, AudioAdjustments, Clip, Marker, Keyframe, Transition, EffectRegion } from "./EditorStore.types";
export type { TimelineState } from "./EditorStore.types";
export { clamp, normalizeEffectRegion } from "./EditorStore.types";
export {
  VIDEO_TRACK_ID, CAMERA_TRACK_ID, AUDIO_TRACK_ID,
  OVERLAY_TRACK_ID, TEXT_TRACK_ID, EFFECTS_TRACK_ID, TRANSITIONS_TRACK_ID,
  PLACEHOLDER_ASSET_ID, MISSING_ASSET_PLACEHOLDER,
  MAX_HISTORY, MAX_EFFECT_REGIONS,
  FX_BLOCK_DEFAULT_CREATE_WINDOW_SEC, FX_BLOCK_DEFAULT_CREATE_WINDOW_EPSILON_SEC,
} from "./EditorStore.types";
export type { HistoryEntry, FxBlockTargetClipWindow } from "./EditorStore.types";

// ── FX creation helpers (extracted → EditorStore.fxHelpers.ts, S2) ──
import {
  isDefaultFxCreateWindow,
  resolveFxBlockCreationTargets,
  resolveFxBlockCreationWindow,
  resolveFxBlockCreationCandidates,
} from "./EditorStore.fxHelpers";
export {
  isDefaultFxCreateWindow,
  resolveFxBlockCreationTargets,
  resolveFxBlockCreationWindow,
  resolveFxBlockCreationCandidates,
} from "./EditorStore.fxHelpers";

let didLogSplitRenderPropsMismatch = false;

const Ctx = createContext<TimelineState | null>(null);
export const useTimeline = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error("useTimeline must be inside TimelineProvider");
  return v;
};

/** Safe version that returns null if outside TimelineProvider */
export const useTimelineSafe = (): TimelineState | null => {
  return useContext(Ctx);
};

export const TimelineProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // 🛡️ FIX: Add store initialization guard to prevent feedback loops
  const storeDidInitRef = React.useRef(false);

  const [assets, setAssets] = useState<Asset[]>([]);

  // 🐛 FIX: Keep assets in a ref to avoid useCallback dependency churn in addClip
  // This prevents infinite loops when upsertAsset is called before addClip
  const assetsRef = React.useRef(assets);
  React.useEffect(() => { assetsRef.current = assets; }, [assets]);

  // Keep an internal track state; we'll expose a filtered "visible" list via context.
  const [tracksState, setTracksState] = useState<Track[]>([
    { id: CAMERA_TRACK_ID, kind: "camera", order: -1, name: "Camera" },
    { id: VIDEO_TRACK_ID, kind: "video", order: 0, name: "Video" },
    { id: AUDIO_TRACK_ID, kind: "audio", order: 1, name: "Audio" },
    { id: OVERLAY_TRACK_ID, kind: "overlay", order: 2, name: "Overlay" },
    { id: TEXT_TRACK_ID, kind: "text", order: 3, name: "Text" },
    { id: EFFECTS_TRACK_ID, kind: "effects", order: 4, name: "Effects" },
    { id: TRANSITIONS_TRACK_ID, kind: "transitions", order: 5, name: "Transitions" },
  ]);
  const [clips, setClips] = useState<Clip[]>([]);

  // 🐛 FIX: Keep clips in a ref to check for position changes without triggering re-renders
  const clipsRef = React.useRef<Clip[]>([]);
  React.useEffect(() => { clipsRef.current = clips; }, [clips]);

  // tracksStateRef placed here (tracksState declared above at line ~525)
  const tracksStateRef = React.useRef(tracksState);
  useEffect(() => { tracksStateRef.current = tracksState; }, [tracksState]);

  // 🔍 DIAGNOSTIC: Track store mounted with init guard
  useEffect(() => {
    if (storeDidInitRef.current) return; // Prevent duplicate initialization
    storeDidInitRef.current = true;

    vb.on('store:mounted');
    vb.count('store:mount');
    return () => {
      vb.on('store:unmounted');
    };
  }, []);
  const [markers] = useState<Marker[]>([]);
  const [keyframes, setKeyframes] = useState<Keyframe[]>([]);
  const [transitions, setTransitions] = useState<Transition[]>([]);
  const [effectRegions, setEffectRegions] = useState<EffectRegion[]>([]);
  const [fxBlocks, setFxBlocks] = useState<FxBlock[]>([]);
  const [selectedFxBlockId, setSelectedFxBlockId] = useState<string | null>(null);

  // Sync-refs for undo/redo (pushToHistory reads from refs to avoid stale closures)
  const keyframesRef = React.useRef(keyframes);
  useEffect(() => { keyframesRef.current = keyframes; }, [keyframes]);
  const transitionsRef = React.useRef(transitions);
  useEffect(() => { transitionsRef.current = transitions; }, [transitions]);
  const effectRegionsRef = React.useRef(effectRegions);
  useEffect(() => { effectRegionsRef.current = effectRegions; }, [effectRegions]);
  const fxBlocksRef = React.useRef(fxBlocks);
  useEffect(() => { fxBlocksRef.current = fxBlocks; }, [fxBlocks]);

  const [playhead, setPlayhead] = useState(0);
  const [isPlaying, setPlaying] = useState(false);
  const [loopPlayback, setLoopPlayback] = useState(false);
  const [loopRegion, setLoopRegionState] = useState<{ start: number; end: number } | null>(null);

  // Timeline comments
  const [commentsEnabled, setCommentsEnabled] = useState(false);
  const [comments, setCommentsState] = useState<TimelineComment[]>([]);

  const [masterMuted, setMasterMuted] = useState(false);
  const [pxPerSec, setPxPerSec] = useState(60); // zoom baseline
  const [zoom, setZoom] = useState(1);

  const [selection, setSelection] = useState<{ clipIds: string[] }>({ clipIds: [] });

  // Undo/Redo history state
  const historyRef = React.useRef<HistoryEntry[]>([]);
  const historyIndexRef = React.useRef<number>(-1);
  const [historyVersion, setHistoryVersion] = useState(0); // Trigger re-renders for canUndo/canRedo
  const [lastEditAt, setLastEditAt] = useState(0);

  const hasVideoClips = useCallback(() => {
    return clips.some(c => c.trackId === VIDEO_TRACK_ID);
  }, [clips]);

  const hasAudioClips = useCallback(() => {
    return clips.some(c => c.trackId === AUDIO_TRACK_ID);
  }, [clips]);

  // 🐛 FIX: Use assetsRef to avoid assets dependency causing callback churn
  const hasOverlayClips = useCallback(() => {
    return clips.some((c) => {
      const asset = assetsRef.current.find(a => a.id === c.assetId);
      return asset?.type === "image" || c.trackId === OVERLAY_TRACK_ID;
    });
  }, [clips]);

  const hasTextClips = useCallback(() => {
    return clips.some((c) => {
      const asset = assetsRef.current.find(a => a.id === c.assetId);
      return asset?.type === "text" || c.trackId === TEXT_TRACK_ID;
    });
  }, [clips]);

  const canAddTrack = useCallback((_kind: Track["kind"]) => {
    if (_kind === "camera") return false;
    // Unlimited tracks per kind
    return true;
  }, []);

  const setLoopRegion = useCallback((start: number, end: number) => {
    const a = Number.isFinite(start) ? Math.max(0, start) : 0;
    const b = Number.isFinite(end) ? Math.max(0, end) : 0;
    const s = Math.min(a, b);
    const e = Math.max(a, b);
    // Require a minimal span to avoid accidental zero-length regions.
    if (e - s < 0.01) {
      setLoopRegionState(null);
      return;
    }
    setLoopRegionState({ start: s, end: e });
  }, []);

  const clearLoopRegion = useCallback(() => {
    setLoopRegionState(null);
  }, []);

  // --- Comment Operations ---
  const addComment = useCallback((timeMs: number, text?: string): string => {
    const id = crypto.randomUUID();
    const newComment: TimelineComment = {
      id,
      timeMs,
      text: text ?? "",
      attachments: [],
      author: "", // TODO: Get from auth context
      createdAt: Date.now(),
      resolved: false,
    };
    setCommentsState((prev) => [...prev, newComment]);
    return id;
  }, []);

  const updateComment = useCallback((id: string, patch: Partial<TimelineComment>) => {
    setCommentsState((prev) =>
      prev.map((c) => (c.id === id ? { ...c, ...patch } : c))
    );
  }, []);

  const deleteComment = useCallback((id: string) => {
    setCommentsState((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const setComments = useCallback((newComments: TimelineComment[]) => {
    setCommentsState(newComments);
  }, []);

  // --- Undo/Redo Implementation ---

  // Push current state to history (call BEFORE making changes)
  const pushToHistory = useCallback(() => {
    const entry: HistoryEntry = {
      clips: freeze([...clipsRef.current], true),
      tracks: freeze([...tracksStateRef.current], true),
      keyframes: freeze([...keyframesRef.current], true),
      transitions: freeze([...transitionsRef.current], true),
      effectRegions: freeze([...effectRegionsRef.current], true),
      fxBlocks: freeze([...fxBlocksRef.current], true),
    };

    // If we're not at the end of history, truncate forward history
    if (historyIndexRef.current < historyRef.current.length - 1) {
      historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1);
    }

    // Add new entry
    historyRef.current.push(entry);
    historyIndexRef.current = historyRef.current.length - 1;

    // Limit history size
    if (historyRef.current.length > MAX_HISTORY) {
      historyRef.current.shift();
      historyIndexRef.current = historyRef.current.length - 1;
    }

    setHistoryVersion((v) => v + 1);
    setLastEditAt(Date.now());
  }, []); // empty deps — reads from refs

  const undo = useCallback(() => {
    if (historyIndexRef.current < 0) return;

    // Save current state for redo if at the end
    if (historyIndexRef.current === historyRef.current.length - 1) {
      const currentEntry: HistoryEntry = {
        clips: freeze([...clipsRef.current], true),
        tracks: freeze([...tracksStateRef.current], true),
        keyframes: freeze([...keyframesRef.current], true),
        transitions: freeze([...transitionsRef.current], true),
        effectRegions: freeze([...effectRegionsRef.current], true),
        fxBlocks: freeze([...fxBlocksRef.current], true),
      };
      historyRef.current.push(currentEntry);
    }

    const entry = historyRef.current[historyIndexRef.current];
    if (!entry) return;

    // Restore state — spread to create mutable copies from frozen snapshots
    setClips([...entry.clips]);
    setTracksState([...entry.tracks]);
    setKeyframes([...entry.keyframes]);
    setTransitions([...entry.transitions]);
    setEffectRegions([...(entry.effectRegions || [])]);
    setFxBlocks([...(entry.fxBlocks || [])]);

    historyIndexRef.current--;
    setHistoryVersion((v) => v + 1);
    setLastEditAt(Date.now());
  }, []); // empty deps — reads from refs

  const redo = useCallback(() => {
    if (historyIndexRef.current >= historyRef.current.length - 1) return;

    historyIndexRef.current++;
    const entry = historyRef.current[historyIndexRef.current];
    if (!entry) return;

    // Restore state — spread to create mutable copies from frozen snapshots
    setClips([...entry.clips]);
    setTracksState([...entry.tracks]);
    setKeyframes([...entry.keyframes]);
    setTransitions([...entry.transitions]);
    setEffectRegions([...(entry.effectRegions || [])]);
    setFxBlocks([...(entry.fxBlocks || [])]);

    setHistoryVersion((v) => v + 1);
    setLastEditAt(Date.now());
  }, []);

  const canUndo = historyIndexRef.current >= 0;
  const canRedo = historyIndexRef.current < historyRef.current.length - 1;

  const removeClipTargetsFromFxBlocks = useCallback((deletedClipIds: Set<string>) => {
    if (deletedClipIds.size === 0) return;
    setFxBlocks((prev) => {
      const next: FxBlock[] = [];
      for (const block of prev) {
        if (!block.targetClipIds?.length) {
          next.push(block);
          continue;
        }
        const targetClipIds = block.targetClipIds.filter((id) => !deletedClipIds.has(id));
        if (targetClipIds.length === 0 && !block.targetTrackIds?.length) continue;
        next.push({ ...block, targetClipIds });
      }
      return next;
    });
  }, []);

  // --- helpers ---
  const ensureAudioTrack = useCallback(() => {
    setTracksState((v) => {
      if (v.some(t => t.id === AUDIO_TRACK_ID)) return v;
      const audioTrack: Track = { id: AUDIO_TRACK_ID, kind: "audio" as const, order: 1, name: "Audio" };
      const next: Track[] = [...v, audioTrack];
      // normalize orders for base tracks
      return next.map((t): Track => {
        if (t.id === CAMERA_TRACK_ID) return { ...t, order: -1 };
        if (t.id === VIDEO_TRACK_ID) return { ...t, order: 0 };
        if (t.id === AUDIO_TRACK_ID) return { ...t, order: 1 };
        if (t.id === OVERLAY_TRACK_ID) return { ...t, order: 2 };
        return t;
      });
    });
  }, []);

  // removeAudioTrackIfEmpty removed (CavaAudit1 D7/L5 — was a no-op)

  const pruneEmptyPerElementTracks = useCallback((nextClips: Clip[]) => {
    const usedTrackIds = new Set(nextClips.map((c) => c.trackId));
    setTracksState((prev) =>
      prev.filter((t) => {
        // Never prune base lanes
        if (
          t.id === CAMERA_TRACK_ID ||
          t.id === VIDEO_TRACK_ID ||
          t.id === AUDIO_TRACK_ID ||
          t.id === OVERLAY_TRACK_ID ||
          t.id === TEXT_TRACK_ID ||
          t.id === EFFECTS_TRACK_ID ||
          t.id === TRANSITIONS_TRACK_ID
        ) {
          return true;
        }
        // Per-element lanes are removable when empty
        if (t.kind === "text" && t.id.startsWith("text-") && !usedTrackIds.has(t.id)) {
          return false;
        }
        if (t.kind === "overlay" && t.id.startsWith("overlay-") && !usedTrackIds.has(t.id)) {
          return false;
        }
        return true;
      })
    );
  }, []);

  const resolveTrackForAsset = useCallback((assetType: Asset["type"] | undefined, incomingTrackId?: string): string => {
    // For overlays/text, honor caller-provided lane IDs (tracks may be added in the same tick).
    if (typeof incomingTrackId === "string") {
      if (!assetType) return incomingTrackId;
      if (assetType === "image") return incomingTrackId;
      if (assetType === "text") return incomingTrackId;

      // For other types, only accept a known existing track.
      const t = tracksState.find((x) => x.id === incomingTrackId);
      if (t) return incomingTrackId;
    }

    // Route core media into their base lanes.
    if (assetType === "video") return VIDEO_TRACK_ID;
    if (assetType === "audio") return AUDIO_TRACK_ID;
    if (assetType === "image") return OVERLAY_TRACK_ID;
    if (assetType === "text") return TEXT_TRACK_ID;
    return VIDEO_TRACK_ID;
  }, [tracksState]);

  // CRUD for assets
  const addAsset = useCallback((a: Asset) => setAssets((v) => [...v, a]), []);
  const upsertAsset = useCallback((a: Asset) => {
    setAssets((v) => {
      const i = v.findIndex((x) => x.id === a.id);
      if (i === -1) return [...v, a];
      const nv = [...v]; nv[i] = { ...nv[i], ...a }; return nv;
    });
  }, []);

  // addTrack: unlimited lanes; UI can create as many as needed
  // 🐛 FIX: Added duplicate check to prevent tracks with same ID being added twice
  const addTrack = useCallback((t: Track) => {
    pushToHistory();
    setTracksState((v) => {
      // Prevent duplicate tracks with same ID
      if (v.some(existing => existing.id === t.id)) {
        return v; // Track already exists, don't add duplicate
      }
      // Auto-generate order
      const newOrder = v.length > 0 ? Math.max(...v.map(x => x.order)) + 1 : 0;
      const newTrack: Track = { ...t, order: t.order ?? newOrder };
      return [...v, newTrack];
    });
  }, [pushToHistory]);

  const removeTrack = useCallback((trackId: string) => {
    pushToHistory();
    // Prevent removing base tracks if they have clips
    if ([VIDEO_TRACK_ID, AUDIO_TRACK_ID, OVERLAY_TRACK_ID, TEXT_TRACK_ID, EFFECTS_TRACK_ID, TRANSITIONS_TRACK_ID].includes(trackId)) {
      const hasClips = clips.some(c => c.trackId === trackId);
      if (hasClips) return; // cannot remove base track with clips
    }
    setTracksState((v) => v.filter(t => t.id !== trackId));
    // Also remove clips on this track
    setClips((v) => v.filter(c => c.trackId !== trackId));
  }, [clips, pushToHistory]);

  const updateTrack = useCallback((trackId: string, patch: Partial<Track>) => {
    pushToHistory();
    setTracksState((v) => v.map(t => t.id === trackId ? { ...t, ...patch } : t));
  }, [pushToHistory]);

  const reorderTracks = useCallback((trackIds: string[]) => {
    pushToHistory();
    setTracksState((v) => {
      const newTracks: Track[] = trackIds
        .map((id, idx) => {
          const track = v.find(t => t.id === id);
          if (!track) return null;
          const reorderedTrack: Track = { ...track, order: idx };
          return reorderedTrack;
        })
        .filter((t): t is Track => t !== null);
      return newTracks;
    });
  }, [pushToHistory]);

  // addClip: route ALL media into the correct base track and handle placeholder fill for video
  // 🐛 FIX: Use assetsRef.current to avoid assets dependency causing infinite loops
  const addClip = useCallback((c: Clip) => {
    pushToHistory();
    setClips((prevClips) => {
      const asset = assetsRef.current.find(a => a.id === c.assetId);

      // Guard: if no asset found, just drop-in but still enforce track routing if track is wrong
      const assetType = asset?.type;

      if (assetType === "video") {
        // Route to video-base, and either fill placeholder or append to end.
        const placeholder = prevClips.find(x =>
          x.trackId === VIDEO_TRACK_ID &&
          (x.assetId === PLACEHOLDER_ASSET_ID || x.name?.toLowerCase() === "placeholder")
        );

        if (placeholder && asset) {
          const dur = Math.max(0.1, asset.duration || (placeholder.end - placeholder.start));
          const newEnd = placeholder.start + dur;
          const newOut = (placeholder.in ?? 0) + dur;

          return prevClips.map(x =>
            x.id === placeholder.id
              ? {
                ...x,
                assetId: asset.id,
                name: asset.name ?? x.name,
                end: newEnd,
                out: newOut,
              }
              : x
          );
        } else {
          const lastEnd = prevClips
            .filter(x => x.trackId === VIDEO_TRACK_ID)
            .reduce((m, x) => Math.max(m, x.end), 0);

          const start = lastEnd;
          // 🐛 FIX #2: Use clip's out duration if available (from insertOrReplaceClip), otherwise asset duration
          // This ensures second+ videos use full duration when added
          const dur = Math.max(0.1, c.out ?? asset?.duration ?? (c.end - c.start));
          const next: Clip = {
            ...c,
            trackId: VIDEO_TRACK_ID,
            start,
            end: start + dur,
            in: c.in ?? 0,
            out: c.out ?? dur, // Preserve the out value from insertOrReplaceClip
          };
          return [...prevClips, next];
        }
      }

      if (assetType === "audio") {
        // Ensure audio-base is present/visible, then append audio at end of audio track.
        ensureAudioTrack();

        const lastEnd = prevClips
          .filter(x => x.trackId === AUDIO_TRACK_ID)
          .reduce((m, x) => Math.max(m, x.end), 0);

        const start = lastEnd;
        const dur = Math.max(0.1, asset?.duration ?? (c.end - c.start));
        const next: Clip = {
          ...c,
          trackId: AUDIO_TRACK_ID,
          start,
          end: start + dur,
          in: c.in ?? 0,
          out: (c.in ?? 0) + dur,
        };
        return [...prevClips, next];
      }

      // Route text + images into their fixed lanes.
      if (assetType === "text" || assetType === "image") {
        const trackId = resolveTrackForAsset(assetType, c.trackId);
        const start = Math.max(0, c.start);
        const dur = Math.max(0.1, (c.out ?? asset?.duration) ?? (c.end - c.start));
        return [...prevClips, { ...c, trackId, start, end: start + dur, out: (c.in ?? 0) + dur }];
      }

      // Fallback for unknown asset types.
      return [...prevClips, { ...c, trackId: resolveTrackForAsset(assetType, c.trackId) }];
    });
  }, [ensureAudioTrack, resolveTrackForAsset, pushToHistory]); // 🐛 FIX: Removed assets - using assetsRef instead

  const syncTemplateBounds = useCallback((clipsArr: Clip[], templateId: string): Clip[] => {
    if (!templateId) return clipsArr;
    const template = clipsArr.find((c) => c.id === templateId);
    if (!template || (template as any).clipType !== "template") return clipsArr;
    const children = clipsArr.filter((c: any) => (c as any).templateClipId === templateId);
    if (children.length === 0) return clipsArr;
    const minStart = Math.max(0, children.reduce((m, c) => Math.min(m, c.start), Infinity));
    const maxEnd = children.reduce((m, c) => Math.max(m, c.end), 0);
    const dur = Math.max(0.1, maxEnd - minStart);
    return clipsArr.map((c) =>
      c.id === templateId
        ? { ...c, start: minStart, end: minStart + dur, out: (c.in ?? 0) + dur }
        : c
    );
  }, []);

  const updateClip = useCallback((clipId: string, patch: Partial<Clip>) => {
    pushToHistory();
    setClips((v) => {
      const next = v.map((c) => (c.id === clipId ? { ...c, ...patch } : c));
      const updated = next.find((c) => c.id === clipId);
      const templateId = (updated as any)?.templateClipId as string | undefined;
      return templateId ? syncTemplateBounds(next, templateId) : next;
    });
  }, [syncTemplateBounds, pushToHistory]);

  const deleteClip = useCallback((clipId: string) => {
    const targetClip = clips.find(c => c.id === clipId);
    const targetAssetId = targetClip?.assetId;
    const otherClipsUseAsset = targetAssetId ? clips.some(c => c.id !== clipId && c.assetId === targetAssetId) : true;

    pushToHistory();
    setClips((v) => {
      const toDelete = new Set<string>([clipId]);
      const target = v.find((x) => x.id === clipId);
      const templateToSync = target && (target as any).clipType !== "template"
        ? ((target as any).templateClipId as string | undefined)
        : undefined;
      if (target && (target as any).clipType === "template") {
        v.forEach((x) => {
          if ((x as any).templateClipId === clipId) toDelete.add(x.id);
        });
      }
      let next = v.filter((c) => !toDelete.has(c.id));
      if (templateToSync) next = syncTemplateBounds(next, templateToSync);
      pruneEmptyPerElementTracks(next);
      return next;
    });
    // 🐛 FIX: Clean up transitions referencing deleted clip
    setTransitions((v) => v.filter((t) => t.fromClipId !== clipId && t.toClipId !== clipId));
    // 🐛 FIX: Clean up keyframes referencing deleted clip
    setKeyframes((v) => v.filter((k) => k.clipId !== clipId));
    removeClipTargetsFromFxBlocks(new Set([clipId]));
    // 🐛 FIX: Clean up orphan assets no longer referenced by any clip
    if (targetAssetId && !otherClipsUseAsset) {
      setAssets(prev => prev.filter(a => a.id !== targetAssetId));
    }
  }, [clips, pruneEmptyPerElementTracks, syncTemplateBounds, pushToHistory, removeClipTargetsFromFxBlocks]);

  const selectClips = useCallback((ids: string[]) => setSelection({ clipIds: ids }), []);
  const selectAllClips = useCallback(() => setSelection({ clipIds: clips.map(c => c.id) }), [clips]);
  const deselectAllClips = useCallback(() => setSelection({ clipIds: [] }), []);
  const deleteSelectedClips = useCallback(() => {
    pushToHistory();
    const toDelete = new Set<string>(selection.clipIds);
    setClips((v) => {
      const templatesToSync = new Set<string>();
      for (const id of selection.clipIds) {
        const clip = v.find((x) => x.id === id);
        const templateId = clip && (clip as any).clipType !== "template"
          ? ((clip as any).templateClipId as string | undefined)
          : undefined;
        if (templateId) templatesToSync.add(templateId);
        if (clip && (clip as any).clipType === "template") {
          v.forEach((x) => {
            if ((x as any).templateClipId === id) toDelete.add(x.id);
          });
        }
      }
      let next = v.filter((c) => !toDelete.has(c.id));
      templatesToSync.forEach((templateId) => {
        next = syncTemplateBounds(next, templateId);
      });
      pruneEmptyPerElementTracks(next);
      return next;
    });
    // 🐛 FIX: Clean up transitions referencing any deleted clips
    setTransitions((v) => v.filter((t) => !toDelete.has(t.fromClipId) && !toDelete.has(t.toClipId)));
    // 🐛 FIX: Clean up keyframes referencing any deleted clips
    setKeyframes((v) => v.filter((k) => !toDelete.has(k.clipId)));
    removeClipTargetsFromFxBlocks(toDelete);
    setSelection({ clipIds: [] });
  }, [selection.clipIds, pruneEmptyPerElementTracks, syncTemplateBounds, pushToHistory, removeClipTargetsFromFxBlocks]);

  // Keyframe operations
  const addKeyframe = useCallback((keyframe: Keyframe) => {
    pushToHistory();
    setKeyframes((v) => [...v, keyframe]);
  }, [pushToHistory]);
  const updateKeyframe = useCallback((id: string, patch: Partial<Keyframe>) => {
    pushToHistory();
    setKeyframes((v) => v.map((k) => (k.id === id ? { ...k, ...patch } : k)));
  }, [pushToHistory]);
  const deleteKeyframe = useCallback((id: string) => {
    pushToHistory();
    setKeyframes((v) => v.filter((k) => k.id !== id));
  }, [pushToHistory]);

  // PHASE 1: Volume keyframe helpers
  const addVolumeKeyframe = useCallback((clipId: string, relativeTime: number, volume: number) => {
    pushToHistory();
    const newKeyframe: Keyframe = {
      id: id(),
      clipId,
      time: Math.max(0, relativeTime),
      property: 'audio.volume',
      value: Math.max(0, Math.min(200, volume)), // clamp 0-200%
      easing: 'linear',
    };
    setKeyframes((v) => [...v, newKeyframe]);
  }, [pushToHistory]);

  const getVolumeKeyframes = useCallback((clipId: string) => {
    return keyframes
      .filter((k) => k.clipId === clipId && k.property === 'audio.volume')
      .sort((a, b) => a.time - b.time);
  }, [keyframes]);

  const getVolumeAtTime = useCallback((clipId: string, relativeTime: number): number | null => {
    const volumeKfs = keyframes
      .filter((k) => k.clipId === clipId && k.property === 'audio.volume')
      .sort((a, b) => a.time - b.time);

    if (volumeKfs.length === 0) return null;

    // Find surrounding keyframes
    let beforeKf: Keyframe | null = null;
    let afterKf: Keyframe | null = null;

    for (let i = 0; i < volumeKfs.length; i++) {
      if (volumeKfs[i].time <= relativeTime) {
        beforeKf = volumeKfs[i];
      }
      if (volumeKfs[i].time >= relativeTime && !afterKf) {
        afterKf = volumeKfs[i];
      }
    }

    // Exact match
    if (beforeKf && beforeKf.time === relativeTime) {
      return typeof beforeKf.value === 'number' ? beforeKf.value : 100;
    }
    if (afterKf && afterKf.time === relativeTime) {
      return typeof afterKf.value === 'number' ? afterKf.value : 100;
    }

    // Before first keyframe
    if (!beforeKf && afterKf) {
      return typeof afterKf.value === 'number' ? afterKf.value : 100;
    }

    // After last keyframe
    if (beforeKf && !afterKf) {
      return typeof beforeKf.value === 'number' ? beforeKf.value : 100;
    }

    // Interpolate between two keyframes
    if (beforeKf && afterKf) {
      const beforeVal = typeof beforeKf.value === 'number' ? beforeKf.value : 100;
      const afterVal = typeof afterKf.value === 'number' ? afterKf.value : 100;
      const t = (relativeTime - beforeKf.time) / (afterKf.time - beforeKf.time);
      return beforeVal + (afterVal - beforeVal) * t;
    }

    return null;
  }, [keyframes]);

  const deleteVolumeKeyframes = useCallback((clipId: string) => {
    pushToHistory();
    setKeyframes((v) => v.filter((k) => !(k.clipId === clipId && k.property === 'audio.volume')));
  }, [pushToHistory]);

  // Transition operations
  const addTransition = useCallback((transition: Transition) => {
    pushToHistory();
    setTransitions((v) => [...v, transition]);
  }, [pushToHistory]);
  const updateTransition = useCallback((id: string, patch: Partial<Transition>) => {
    pushToHistory();
    setTransitions((v) => v.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  }, [pushToHistory]);
  const deleteTransition = useCallback((id: string) => {
    pushToHistory();
    setTransitions((v) => v.filter((t) => t.id !== id));
  }, [pushToHistory]);

  const addFxBlock = useCallback((fxBlock: FxBlock) => {
    const parsed = FxBlockSchema.safeParse(fxBlock);
    if (!parsed.success) return;
    const candidateWindows = resolveFxBlockCreationCandidates({
      fxBlock: parsed.data,
      clips,
      selectedClipIds: selection?.clipIds || [],
      playhead,
    });
    const candidateBlocks = candidateWindows.map((candidate) => ({
      ...parsed.data,
      id: candidate.id,
      start: candidate.start,
      end: candidate.end,
      targetClipIds: candidate.targetClipIds,
    }));

    const nextBlocks = candidateBlocks.flatMap((candidate) => {
      const parsedCandidate = FxBlockSchema.safeParse(candidate);
      return parsedCandidate.success ? [parsedCandidate.data] : [];
    });
    if (nextBlocks.length === 0) return;

    pushToHistory();
    setFxBlocks((v) => [...v, ...nextBlocks]);
  }, [clips, playhead, pushToHistory, selection?.clipIds]);

  const updateFxBlock = useCallback((id: string, patch: Partial<FxBlock>) => {
    pushToHistory();
    setFxBlocks((v) =>
      v.map((block) => {
        if (block.id !== id) return block;
        const merged = { ...block, ...patch };
        const parsed = FxBlockSchema.safeParse(merged);
        return parsed.success ? parsed.data : block;
      })
    );
  }, [pushToHistory]);

  const deleteFxBlock = useCallback((id: string) => {
    pushToHistory();
    setFxBlocks((v) => v.filter((block) => block.id !== id));
  }, [pushToHistory]);

  const getActiveFxBlocksAtTime = useCallback((t: number): FxBlock[] => {
    const activeById = new Map<string, FxBlock>();
    for (const fx of fxBlocks) {
      if (!fx.enabled) continue;
      if (!(t + 1 / 240 >= fx.start && t <= fx.end + 1 / 240)) continue;
      if (activeById.has(fx.id)) activeById.delete(fx.id);
      activeById.set(fx.id, fx);
    }
    const active = [...activeById.values()];
    const trackOrderById = new Map<string, number>(tracksState.map((track) => [track.id, track.order ?? 0]));
    return sortFxBlocksByTrackOrder(active, trackOrderById);
  }, [fxBlocks, tracksState]);

  // Effect Region operations
  const addEffectRegion = useCallback((region: EffectRegion) => {
    if (effectRegions.length >= MAX_EFFECT_REGIONS) return;
    pushToHistory();
    const normalized = normalizeEffectRegion(region) ?? {
      id: region?.id || crypto.randomUUID(),
      startMs: 0,
      endMs: 1000,
      targetClipIds: undefined,
      targetClipId: undefined,
      targetTrackIds: undefined,
      loopEnabled: false,
      volumeMultiplier: 1,
      playbackRate: 1,
      rampMs: 0,
      name: undefined,
    };

    // Scoping priority: track-based > clip-based
    // If targetTrackIds is already set, use track scoping
    if (normalized.targetTrackIds?.length) {
      // Track-scoped region - no need for clip IDs
      setEffectRegions((v) => [...v, normalized]);
      return;
    }

    // Otherwise, bind to current selection (clip-scoped)
    if (!normalized.targetClipIds?.length) {
      const ids = (selection?.clipIds || []).filter(Boolean);
      if (ids.length > 0) {
        normalized.targetClipIds = ids;
        normalized.targetClipId = ids[0]; // backward compat
      }
    }

    // Must have either track or clip scoping
    if (!normalized.targetClipIds?.length && !normalized.targetTrackIds?.length) {
      console.warn('[EditorStore] addEffectRegion: no clip or track target — select elements first');
      return;
    }
    setEffectRegions((v) => [...v, normalized]);
  }, [pushToHistory, effectRegions.length, selection?.clipIds, clips]);

  const updateEffectRegion = useCallback((regionId: string, patch: Partial<EffectRegion>) => {
    pushToHistory();
    setEffectRegions((v) =>
      v.map((r) => {
        if (r.id !== regionId) return r;
        const merged: any = { ...r, ...patch };
        return normalizeEffectRegion(merged) ?? r;
      })
    );
  }, [pushToHistory]);

  const deleteEffectRegion = useCallback((regionId: string) => {
    pushToHistory();
    setEffectRegions((v) => v.filter((r) => r.id !== regionId));
  }, [pushToHistory]);

  /**
   * Get the combined effect modifiers at a given timeline time.
   * If multiple regions overlap, returns the first match (priority by order).
   * Returns null if playhead is not inside any effect region.
   *
   * Scoping priority:
   * 1. If targetTrackIds is set, applies to all clips on those tracks (track-scoped)
   * 2. Otherwise, if targetClipIds is set, applies to specific clips (clip-scoped)
   * 3. If neither is set, returns null (no global effects)
   */
  const getEffectAtTime = useCallback((t: number): {
    volume?: number;
    speed?: number;
    rampMs?: number;
    regionId?: string;
    targetClipId?: string;
    targetClipIds?: string[];
    targetTrackIds?: string[];
  } | null => {
    const ttMs = Math.floor(Math.max(0, t) * 1000);
    let match: EffectRegion | null = null;
    // Prefer later regions if overlaps exist (last-added wins).
    for (let i = effectRegions.length - 1; i >= 0; i--) {
      const r = effectRegions[i];
      if (!r) continue;
      if (ttMs >= r.startMs && ttMs < r.endMs) {
        match = r;
        break;
      }
    }
    if (!match) return null;

    // Track-scoped: resolve targetTrackIds to clip IDs
    let clipIds: string[] = [];
    let trackIds: string[] = [];

    if (match.targetTrackIds?.length) {
      // Track-scoped: apply to all clips on the target tracks
      trackIds = match.targetTrackIds;
      clipIds = clipsRef.current
        .filter(c => trackIds.includes(c.trackId))
        .map(c => c.id);
    } else {
      // Clip-scoped: use explicit clip IDs
      clipIds = match.targetClipIds?.length ? match.targetClipIds : (match.targetClipId ? [match.targetClipId] : []);
    }

    // Never return global effects; Region FX must be scoped (clip or track).
    if (!clipIds.length && !trackIds.length) return null;

    const volPct = Math.round(clamp(match.volumeMultiplier, 0, 2) * 100);
    const speed = clamp(match.playbackRate, 0.25, 4);

    const hasVol = Math.abs(volPct - 100) > 0.5;
    const hasSpeed = Math.abs(speed - 1) > 1e-6;

    if (!hasVol && !hasSpeed) return null;
    return {
      regionId: match.id,
      targetClipId: clipIds[0], // backward compat
      targetClipIds: clipIds,
      targetTrackIds: trackIds.length ? trackIds : undefined,
      volume: hasVol ? volPct : undefined,
      speed: hasSpeed ? speed : undefined,
      rampMs: clamp(match.rampMs || 0, 0, 1000),
    };
  }, [effectRegions]);

  // Editing ops
  // 🐛 FIX: Check if position actually changed to prevent unnecessary state updates and infinite loops
  const moveClip = useCallback((clipId: string, newStart: number, ripple: boolean = false) => {
    // Pre-check: bail out early if the clip position won't actually change
    const existingClip = clipsRef.current.find((x) => x.id === clipId);
    if (!existingClip) return;

    const ns = Math.max(0, newStart);
    const eps = 0.001; // tolerance for floating point comparison

    // If position hasn't meaningfully changed, skip the update entirely
    if (Math.abs(existingClip.start - ns) < eps) return;

    pushToHistory();
    setClips((v) => {
      const c = v.find((x) => x.id === clipId);
      if (!c) return v;
      if ((c as any).clipType === "template") {
        const len = c.end - c.start;
        const delta = ns - c.start;
        return v.map((x) => {
          if (x.id === clipId) return { ...x, start: ns, end: ns + len };
          if ((x as any).templateClipId === clipId) return { ...x, start: x.start + delta, end: x.end + delta };
          return x;
        });
      }
      const len = c.end - c.start;
      const delta = ns - c.start;

      if (!ripple) {
        // Standard move (no ripple)
        const next = v.map((x) => (x.id === clipId ? { ...x, start: ns, end: ns + len } : x));
        const templateId = (c as any).templateClipId as string | undefined;
        return templateId ? syncTemplateBounds(next, templateId) : next;
      }

      // Ripple mode: shift all clips on same track after this clip
      const next = v.map((x) => {
        if (x.id === clipId) return { ...x, start: ns, end: ns + len };
        if (x.trackId === c.trackId && x.start >= c.end) {
          return { ...x, start: x.start + delta, end: x.end + delta };
        }
        return x;
      });
      const templateId = (c as any).templateClipId as string | undefined;
      return templateId ? syncTemplateBounds(next, templateId) : next;
    });
  }, [syncTemplateBounds, pushToHistory]);

  // 🐛 FIX: Use assetsRef to avoid assets dependency causing callback churn
  // 🐛 FIX: Check if trim value changed to prevent unnecessary state updates
  const trimLeft = useCallback((clipId: string, newStart: number, ripple: boolean = false) => {
    // Pre-check: bail out early if the trim won't actually change anything
    const existingClip = clipsRef.current.find((x) => x.id === clipId);
    if (!existingClip || (existingClip as any).clipType === "template") return;
    const asset = assetsRef.current.find((a) => a.id === existingClip.assetId);
    if (!asset) return;

    const minDur = 0.1;
    const ns = clamp(newStart, 0, existingClip.end - minDur);
    const eps = 0.001;

    // If start hasn't meaningfully changed, skip the update
    if (Math.abs(existingClip.start - ns) < eps) return;

    pushToHistory();
    setClips((v) => {
      const c = v.find((x) => x.id === clipId);
      if (!c) return v;
      if ((c as any).clipType === "template") return v;
      const assetInner = assetsRef.current.find((a) => a.id === c.assetId);
      if (!assetInner) return v;

      const delta = ns - c.start;
      const nin = clamp(c.in + delta, 0, assetInner.duration - minDur);

      // FIX: Explicitly preserve end and out - left trim ONLY affects start and in
      // The right edge (end, out) must remain unchanged
      const preservedEnd = c.end;
      const preservedOut = c.out;

      if (!ripple) {
        // Standard trim - ONLY change start and in, preserve end and out explicitly
        const next = v.map((x) => (x.id === clipId ? {
          ...x,
          start: ns,
          in: nin,
          end: preservedEnd,  // Explicitly preserve
          out: preservedOut   // Explicitly preserve
        } : x));
        const templateId = (c as any).templateClipId as string | undefined;
        return templateId ? syncTemplateBounds(next, templateId) : next;
      }

      // Ripple mode: shift all clips on same track after this clip
      const next = v.map((x) => {
        if (x.id === clipId) return {
          ...x,
          start: ns,
          in: nin,
          end: preservedEnd,  // Explicitly preserve
          out: preservedOut   // Explicitly preserve
        };
        if (x.trackId === c.trackId && x.start >= c.end) {
          return { ...x, start: x.start + delta, end: x.end + delta };
        }
        return x;
      });
      const templateId = (c as any).templateClipId as string | undefined;
      return templateId ? syncTemplateBounds(next, templateId) : next;
    });
  }, [syncTemplateBounds, pushToHistory]); // 🐛 FIX: Removed assets - using assetsRef instead

  // 🐛 FIX: Use assetsRef to avoid assets dependency causing callback churn
  // 🐛 FIX: Check if trim value changed to prevent unnecessary state updates
  // ✅ FIX: Allow infinite extension for images/text/shapes (only videos/audio have duration limits)
  const trimRight = useCallback((clipId: string, newEnd: number, ripple: boolean = false) => {
    // Pre-check: bail out early if the trim won't actually change anything
    const existingClip = clipsRef.current.find((x) => x.id === clipId);
    if (!existingClip || (existingClip as any).clipType === "template") return;
    const asset = assetsRef.current.find((a) => a.id === existingClip.assetId);
    if (!asset) return;

    const minDur = 0.1;
    // Images, text, shapes can extend indefinitely (no source duration limit)
    // Videos and audio are limited by their source duration
    const isExtendable = asset.type === "image" || !asset.duration || asset.duration <= 0;
    const maxEnd = isExtendable
      ? Number.MAX_SAFE_INTEGER // "Infinite" for images/text/shapes
      : existingClip.start + (asset.duration - existingClip.in);
    const ne = clamp(newEnd, existingClip.start + minDur, maxEnd);
    const eps = 0.001;

    // If end hasn't meaningfully changed, skip the update
    if (Math.abs(existingClip.end - ne) < eps) return;

    pushToHistory();
    setClips((v) => {
      const c = v.find((x) => x.id === clipId);
      if (!c) return v;
      if ((c as any).clipType === "template") return v;
      const assetInner = assetsRef.current.find((a) => a.id === c.assetId);
      if (!assetInner) return v;

      const nout = c.in + (ne - c.start);
      const delta = ne - c.end;

      if (!ripple) {
        // Standard trim
        const next = v.map((x) => (x.id === clipId ? { ...x, end: ne, out: nout } : x));
        const templateId = (c as any).templateClipId as string | undefined;
        return templateId ? syncTemplateBounds(next, templateId) : next;
      }

      // Ripple mode: shift all clips on same track after this clip
      const next = v.map((x) => {
        if (x.id === clipId) return { ...x, end: ne, out: nout };
        if (x.trackId === c.trackId && x.start >= c.end) {
          return { ...x, start: x.start + delta, end: x.end + delta };
        }
        return x;
      });
      const templateId = (c as any).templateClipId as string | undefined;
      return templateId ? syncTemplateBounds(next, templateId) : next;
    });
  }, [syncTemplateBounds, pushToHistory]); // 🐛 FIX: Removed assets - using assetsRef instead

  const rippleDelete = useCallback((clipId: string) => {
    pushToHistory();
    // Capture clip info before deletion for cleanup
    const clipInfo = clipsRef.current.find((x) => x.id === clipId);
    const clipDuration = clipInfo ? clipInfo.end - clipInfo.start : 0;
    const clipEnd = clipInfo?.end ?? 0;
    const clipTrackId = clipInfo?.trackId;

    setClips((v) => {
      const c = v.find((x) => x.id === clipId);
      if (!c) return v;
      if ((c as any).clipType === "template") {
        const toDelete = new Set<string>([clipId]);
        v.forEach((x) => {
          if ((x as any).templateClipId === clipId) toDelete.add(x.id);
        });
        const next = v.filter((x) => !toDelete.has(x.id));
        pruneEmptyPerElementTracks(next);
        return next;
      }
      const templateToSync = (c as any).templateClipId as string | undefined;

      // Remove clip and shift all clips on same track after it
      let next = v
        .filter((x) => x.id !== clipId)
        .map((x) => {
          if (x.trackId === c.trackId && x.start >= c.end) {
            return { ...x, start: x.start - clipDuration, end: x.end - clipDuration };
          }
          return x;
        });
      if (templateToSync) next = syncTemplateBounds(next, templateToSync);
      pruneEmptyPerElementTracks(next);
      return next;
    });

    // 🐛 FIX: Clean up transitions referencing deleted clip
    setTransitions((v) => v.filter((t) => t.fromClipId !== clipId && t.toClipId !== clipId));

    // 🐛 FIX: Clean up keyframes referencing deleted clip
    setKeyframes((v) => v.filter((k) => k.clipId !== clipId));

    // 🐛 FIX: Update effect regions - remove references to deleted clip and shift timing
    if (clipDuration > 0) {
      const shiftMs = clipDuration * 1000;
      const clipEndMs = clipEnd * 1000;
      setEffectRegions((v) => {
        const updated: EffectRegion[] = [];
        for (const r of v) {
          // Remove the deleted clip from targetClipIds
          const targetClipIds = r.targetClipIds?.filter((id) => id !== clipId);
          // If no targets left after removal and primary target is the deleted clip, skip this region
          if (!targetClipIds?.length && r.targetClipId === clipId) {
            continue;
          }
          // Shift timing for regions after the deleted clip
          const needsShift = r.startMs >= clipEndMs;
          updated.push({
            ...r,
            targetClipIds: targetClipIds?.length ? targetClipIds : r.targetClipIds,
            targetClipId: r.targetClipId === clipId ? (targetClipIds?.[0]) : r.targetClipId,
            startMs: needsShift ? Math.max(0, r.startMs - shiftMs) : r.startMs,
            endMs: needsShift ? Math.max(0, r.endMs - shiftMs) : r.endMs,
          });
        }
        return updated;
      });
    }
    removeClipTargetsFromFxBlocks(new Set([clipId]));
  }, [pruneEmptyPerElementTracks, syncTemplateBounds, pushToHistory, removeClipTargetsFromFxBlocks]);

  // 🐛 FIX: Use assetsRef to avoid assets dependency causing callback churn
  const slipEdit = useCallback((clipId: string, deltaIn: number) => {
    pushToHistory();
    setClips((v) => {
      const c = v.find((x) => x.id === clipId);
      if (!c) return v;
      const asset = assetsRef.current.find((a) => a.id === c.assetId);
      if (!asset) return v;

      // Slip: adjust in/out while keeping start/end fixed
      const clipDuration = c.end - c.start;
      const newIn = clamp(c.in + deltaIn, 0, asset.duration - clipDuration);
      const newOut = newIn + clipDuration;

      return v.map((x) => (x.id === clipId ? { ...x, in: newIn, out: newOut } : x));
    });
  }, [pushToHistory]); // 🐛 FIX: Removed assets - using assetsRef instead

  const splitClipAt = useCallback((clipId: string, t: number) => {
    pushToHistory();
    setClips((v) => {
      const c = v.find((x) => x.id === clipId);
      if (!c) return v;
      if ((c as any).clipType === "template") return v;
      if (t <= c.start || t >= c.end) return v;

      const base: Clip = {
        ...c,
        color: c.color ? { ...c.color } : undefined,
        audio: c.audio ? { ...c.audio } : undefined,
      };

      const leftOut = c.in + (t - c.start);
      const left: Clip = {
        ...base,
        end: t,
        out: leftOut,
        // Keep original elementId for left clip
      };
      // LAYER = CLIP INVARIANT: Each clip gets its own unique canvas layer
      // Split creates two independent layers with separate transforms and z-index
      // This enables unlimited overlays and proper layering
      const right: Clip = {
        ...base,
        id: id(),
        start: t,
        in: leftOut,
        // B1 canary (2026-04-29): share elementId with the left half so both
        // clips bind to the SAME HTMLVideoElement / decoder pipeline. Without
        // this, the right half got a fresh elementId → fresh video element →
        // fresh decoder, producing a ~100ms flash + audio warmup at the split
        // boundary as the new decoder spun up. Sharing means the playhead
        // crosses the split with zero decoder work — matches CapCut/OpenCut
        // behavior. Trade-off: split halves now share canvas transforms.
        // If the user needs independent transforms per half, a future "Detach"
        // action can fork the elementId on demand.
        elementId: c.elementId,
      };

      if (import.meta.env.DEV && !didLogSplitRenderPropsMismatch) {
        const renderProps = (clip: Clip) => {
          const { id: _id, start: _start, end: _end, in: _in, out: _out, elementId: _el, ...rest } = clip;
          return rest;
        };
        const baseStr = JSON.stringify(renderProps(c));
        if (baseStr !== JSON.stringify(renderProps(left)) || baseStr !== JSON.stringify(renderProps(right))) {
          didLogSplitRenderPropsMismatch = true;
          console.warn("[editor2][split] render props mismatch after split (expected only time bounds to change)");
        }
      }

      // 🐛 FIX: Copy cached filmstrip thumbs from the original clipId to the new right clipId.
      // The stable thumb cache is keyed by `${clipId}:${timeMs}` and the nearest-neighbor
      // fallback in getStableThumbsForClip only matches keys starting with `${clipId}:`.
      // Without this copy, the right half of a split renders blank tiles for hundreds of ms
      // until the background regeneration effect in Timeline.tsx catches up.
      // Cache values are JPEG dataURLs / blob URLs; copying the string is safe (no double-revoke).
      try {
        const srcPrefix = `${c.id}:`;
        const dstPrefix = `${right.id}:`;
        const toCopy: Array<[string, string]> = [];
        for (const key of stableThumbCache.keys()) {
          if (key.startsWith(srcPrefix)) {
            toCopy.push([key, dstPrefix + key.slice(srcPrefix.length)]);
          }
        }
        for (const [fromKey, toKey] of toCopy) {
          const val = stableThumbCache.get(fromKey);
          if (val) stableThumbCache.set(toKey, val);
        }
        const status = clipCacheStatus.get(c.id);
        if (status) clipCacheStatus.set(right.id, { ...status });
      } catch {
        // Cache copy is best-effort — regeneration effect will backfill if this fails.
      }

      const out: Clip[] = [];
      for (const x of v) {
        if (x.id !== clipId) out.push(x);
        else out.push(left, right);
      }
      return out;
    });
  }, [pushToHistory]);

  const normalizeTrackOrder = useCallback((tracks: Track[]): Track[] => {
    return tracks
      .map((t): Track => {
        if (t.id === CAMERA_TRACK_ID) return { ...t, order: -1, kind: "camera" as const, name: t.name || "Camera", height: t.height ?? "small" };
        if (t.id === VIDEO_TRACK_ID) return { ...t, order: 0, kind: "video" as const, name: t.name || "Video" };
        if (t.id === AUDIO_TRACK_ID) return { ...t, order: 1, kind: "audio" as const, name: t.name || "Audio", height: t.height ?? "small" };
        if (t.id === OVERLAY_TRACK_ID) return { ...t, order: 2, kind: "overlay" as const, name: t.name || "Overlay", height: t.height ?? "small" };
        if (t.id === TEXT_TRACK_ID) return { ...t, order: 3, kind: "text" as const, name: t.name || "Text", height: t.height ?? "small" };
        if (t.id === EFFECTS_TRACK_ID) return { ...t, order: 4, kind: "effects" as const, name: t.name || "Effects", height: t.height ?? "small" };
        if (t.id === TRANSITIONS_TRACK_ID) return { ...t, order: 5, kind: "transitions" as const, name: t.name || "Transitions", height: t.height ?? "small" };
        return t;
      })
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, []);

  const hydrateFromManifest = useCallback((manifest: {
    assets?: Asset[];
    clips?: Clip[];
    tracks?: Track[];
    bindings?: any[];
    keyframes?: unknown[];
    transitions?: unknown[];
    effectRegions?: unknown[];
    fxBlocks?: unknown[];
  }) => {
    historyRef.current = [];
    historyIndexRef.current = -1;

    const incomingAssets = Array.isArray(manifest?.assets) ? manifest.assets : [];
    const incomingTracks = Array.isArray(manifest?.tracks) ? manifest.tracks : [];
    const incomingClips = Array.isArray(manifest?.clips) ? manifest.clips : [];

    const nextAssets: Asset[] = [];
    for (const a of incomingAssets) {
      if (!a) continue;
      const id = typeof a.id === "string" ? a.id : null;
      const src = typeof a.src === "string" ? a.src : "";
      if (!id) continue;
      nextAssets.push({
        id,
        type: a.type === "audio" || a.type === "image" || a.type === "text" ? a.type : "video",
        src: src || MISSING_ASSET_PLACEHOLDER,
        duration: Number.isFinite((a as any).duration) ? Math.max(0, Number((a as any).duration)) : 0,
        name: typeof a.name === "string" ? a.name : undefined,
        w: typeof (a as any).w === "number" ? (a as any).w : undefined,
        h: typeof (a as any).h === "number" ? (a as any).h : undefined,
        hasAudio: typeof (a as any).hasAudio === "boolean" ? (a as any).hasAudio : undefined,
        previewUrl: typeof (a as any).previewUrl === "string" ? (a as any).previewUrl : undefined,
        sourceUrl: typeof (a as any).sourceUrl === "string" ? (a as any).sourceUrl : undefined,
        uploadId: typeof (a as any).uploadId === "string" ? (a as any).uploadId : undefined,
        sourceUploadId: typeof (a as any).sourceUploadId === "string" ? (a as any).sourceUploadId : undefined,
        preparedUploadId: typeof (a as any).preparedUploadId === "string" ? (a as any).preparedUploadId : undefined,
        posterUrl: typeof (a as any).posterUrl === "string" ? (a as any).posterUrl : undefined,
        preparedMedia: (a as any).preparedMedia ?? undefined,
        deferVideoHydration: typeof (a as any).deferVideoHydration === "boolean" ? (a as any).deferVideoHydration : undefined,
      });
    }

    const assetById = new Map<string, Asset>();
    nextAssets.forEach((a) => assetById.set(a.id, a));

    const baseTracks: Track[] = [
      { id: CAMERA_TRACK_ID, kind: "camera", order: -1, name: "Camera", height: "small" },
      { id: VIDEO_TRACK_ID, kind: "video", order: 0, name: "Video" },
      { id: AUDIO_TRACK_ID, kind: "audio", order: 1, name: "Audio", height: "small" },
      { id: OVERLAY_TRACK_ID, kind: "overlay", order: 2, name: "Overlay", height: "small" },
      { id: TEXT_TRACK_ID, kind: "text", order: 3, name: "Text", height: "small" },
      { id: EFFECTS_TRACK_ID, kind: "effects", order: 4, name: "Effects", height: "small" },
      { id: TRANSITIONS_TRACK_ID, kind: "transitions", order: 5, name: "Transitions", height: "small" },
    ];

    const combinedTracks: Track[] = [];
    for (const t of [...baseTracks, ...incomingTracks]) {
      if (!t) continue;
      const id = typeof t.id === "string" ? t.id : null;
      if (!id) continue;
      if (combinedTracks.some((x) => x.id === id)) continue;
      const kind: Track["kind"] =
        t.kind === "camera" || t.id === CAMERA_TRACK_ID
          ? "camera"
          : t.kind === "audio" || t.id === AUDIO_TRACK_ID
            ? "audio"
            : t.kind === "overlay" || t.id === OVERLAY_TRACK_ID
              ? "overlay"
              : t.kind === "text" || t.id === TEXT_TRACK_ID
                ? "text"
                : t.kind === "effects" || t.id === EFFECTS_TRACK_ID
                  ? "effects"
                  : t.kind === "transitions" || t.id === TRANSITIONS_TRACK_ID
                    ? "transitions"
                    : "video";
      combinedTracks.push({
        id,
        kind,
        order: typeof t.order === "number" ? t.order : combinedTracks.length,
        name: typeof t.name === "string" ? t.name : undefined,
        height: (t as any).height === "small" || (t as any).height === "medium" || (t as any).height === "large"
          ? (t as any).height
          : kind === "video"
            ? undefined
            : "small",
      });
    }

    const combinedTrackIdSet = new Set(combinedTracks.map((t) => t.id));
    let nextDynamicOrder = combinedTracks.reduce((m, t) => Math.max(m, t.order ?? 0), 0) + 1;

    const ensureTrack = (id: string, kind: Track["kind"], name?: string): string => {
      if (combinedTrackIdSet.has(id)) return id;
      combinedTrackIdSet.add(id);
      combinedTracks.push({
        id,
        kind,
        order: nextDynamicOrder++,
        name,
        height: kind === "video" ? undefined : "small",
      });
      return id;
    };

    const getOrCreateTrackIdForClip = (clipId: string, assetId: string, incomingTrackId: any): string => {
      const asset = assetById.get(assetId);
      const assetType = asset?.type;

      // Preserve the main video track behavior.
      if (assetType === "video") return VIDEO_TRACK_ID;
      if (assetType === "audio") return AUDIO_TRACK_ID;

      // If the clip references an explicit track, create it if missing.
      if (typeof incomingTrackId === "string" && incomingTrackId) {
        // Never place non-video layers on the main video track during hydration.
        if (incomingTrackId === VIDEO_TRACK_ID) {
          // fall through to per-layer lane creation below
        } else {
          const existing = combinedTracks.find((t) => t.id === incomingTrackId);
          if (existing) return existing.id;

          const inferredKind: Track["kind"] =
            assetType === "text"
              ? "text"
              : assetType === "image"
                ? "overlay"
                : incomingTrackId.startsWith("text-")
                  ? "text"
                  : incomingTrackId.startsWith("overlay-")
                    ? "overlay"
                    : "overlay";
          return ensureTrack(incomingTrackId, inferredKind);
        }
      }

      // No valid track id: create a dedicated lane per layer (CapCut/Adobe-style).
      if (assetType === "text") return ensureTrack(`text-${clipId.slice(0, 8)}`, "text", asset?.name || "Text");
      if (assetType === "image") return ensureTrack(`overlay-${clipId.slice(0, 8)}`, "overlay", asset?.name || "Overlay");

      // Unknown: keep safe default.
      return VIDEO_TRACK_ID;
    };

    const normalizedClips: Clip[] = [];
    for (const c of incomingClips) {
      if (!c) continue;
      const id = typeof c.id === "string" ? c.id : null;
      const assetId = typeof c.assetId === "string" ? c.assetId : null;
      if (!id || !assetId) continue;

      // Restore audio adjustments if present
      const rawAudio = (c as any).audio;
      let audio: AudioAdjustments | undefined;
      if (rawAudio && typeof rawAudio === "object") {
        audio = {
          volume: typeof rawAudio.volume === "number" ? rawAudio.volume : undefined,
          fadeIn: typeof rawAudio.fadeIn === "number" ? rawAudio.fadeIn : undefined,
          fadeOut: typeof rawAudio.fadeOut === "number" ? rawAudio.fadeOut : undefined,
          ducking: typeof rawAudio.ducking === "boolean" ? rawAudio.ducking : undefined,
          eq: rawAudio.eq === "voice" || rawAudio.eq === "bass" || rawAudio.eq === "treble" || rawAudio.eq === "none"
            ? rawAudio.eq
            : undefined,
        };
        // Only keep if at least one field is set
        if (Object.values(audio).every(v => v === undefined)) audio = undefined;
      }

      // Restore color adjustments if present
      const rawColor = (c as any).color;
      let color: ColorAdjustments | undefined;
      if (rawColor && typeof rawColor === "object") {
        color = {
          brightness: typeof rawColor.brightness === "number" ? rawColor.brightness : undefined,
          contrast: typeof rawColor.contrast === "number" ? rawColor.contrast : undefined,
          saturation: typeof rawColor.saturation === "number" ? rawColor.saturation : undefined,
          exposure: typeof rawColor.exposure === "number" ? rawColor.exposure : undefined,
          highlights: typeof rawColor.highlights === "number" ? rawColor.highlights : undefined,
          shadows: typeof rawColor.shadows === "number" ? rawColor.shadows : undefined,
          temperature: typeof rawColor.temperature === "number" ? rawColor.temperature : undefined,
        };
        // Only keep if at least one field is set
        if (Object.values(color).every(v => v === undefined)) color = undefined;
      }

      normalizedClips.push({
        id,
        assetId,
        elementId: typeof c.elementId === "string" ? c.elementId : undefined,
        parentClipId: typeof (c as any).parentClipId === "string" ? (c as any).parentClipId : undefined,
        templateClipId: typeof (c as any).templateClipId === "string" ? (c as any).templateClipId : undefined,
        clipType: (c as any).clipType === "template" ? "template" : undefined,
        trackId: getOrCreateTrackIdForClip(id, assetId, c.trackId),
        start: Number.isFinite(c.start) ? Number(c.start) : 0,
        end: Number.isFinite(c.end) ? Number(c.end) : Math.max(0.1, (Number(c.start) || 0) + 5),
        in: Number.isFinite(c.in) ? Number(c.in) : 0,
        out: Number.isFinite(c.out) ? Number(c.out) : Number.isFinite(c.end) && Number.isFinite(c.start) ? Number(c.end) - Number(c.start) : 5,
        name: typeof c.name === "string" ? c.name : undefined,
        locked: !!(c as any).locked,
        muted: !!(c as any).muted,
        removeOriginalAudio: !!(c as any).removeOriginalAudio,
        // Restore speed/pitch settings
        speed: typeof (c as any).speed === "number" && (c as any).speed > 0 ? (c as any).speed : undefined,
        maintainPitch: typeof (c as any).maintainPitch === "boolean" ? (c as any).maintainPitch : undefined,
        // Restore audio and color adjustments
        audio,
        color,
      });
    }

    for (const c of normalizedClips) {
      if (!assetById.has(c.assetId)) {
        const fallback: Asset = {
          id: c.assetId || crypto.randomUUID(),
          type: "image",
          src: MISSING_ASSET_PLACEHOLDER,
          duration: Math.max(0.1, (c.end ?? 0) - (c.start ?? 0) || 5),
          name: "Missing Media",
        };
        assetById.set(fallback.id, fallback);
        nextAssets.push(fallback);
      }
    }

    const needsAudioTrack = normalizedClips.some((c) => c.trackId === AUDIO_TRACK_ID);
    if (needsAudioTrack && !combinedTracks.some((t) => t.id === AUDIO_TRACK_ID)) {
      combinedTracks.push({ id: AUDIO_TRACK_ID, kind: "audio", order: 1, name: "Audio" });
    }

    setAssets(nextAssets);
    setTracksState(normalizeTrackOrder(combinedTracks));
    setClips(normalizedClips);
    setKeyframes(Array.isArray((manifest as any).keyframes) ? ((manifest as any).keyframes as Keyframe[]) : []);
    setTransitions(
      Array.isArray((manifest as any).transitions)
        ? ((manifest as any).transitions as Transition[]).filter(
            (transition) =>
              !!transition &&
              typeof transition.id === "string" &&
              typeof transition.fromClipId === "string" &&
              typeof transition.toClipId === "string"
          )
        : []
    );
    setEffectRegions(
      Array.isArray((manifest as any).effectRegions)
        ? ((manifest as any).effectRegions as unknown[])
            .map((region) => normalizeEffectRegion(region))
            .filter((region): region is EffectRegion => !!region)
        : []
    );
    setFxBlocks(normalizeFxBlocks((manifest as any).fxBlocks));
    setSelection({ clipIds: [] });
    setPlayhead(0);
  }, [normalizeTrackOrder]);

  const clearTimeline = useCallback(() => {
    pushToHistory();
    setAssets([]);
    setClips([]);
    setTracksState([
      { id: CAMERA_TRACK_ID, kind: "camera", order: -1, name: "Camera" },
      { id: VIDEO_TRACK_ID, kind: "video", order: 0, name: "Video" },
      { id: "audio-base", kind: "audio", order: 1, name: "Audio" },
      { id: "overlay-base", kind: "overlay", order: 2, name: "Overlay" },
      { id: "text-base", kind: "text", order: 3, name: "Text" },
      { id: "effects-base", kind: "effects", order: 4, name: "Effects" },
      { id: "transitions-base", kind: "transitions", order: 5, name: "Transitions" },
    ]);
    setKeyframes([]);
    setTransitions([]);
    setEffectRegions([]);
    setFxBlocks([]);
    setPlayhead(0);
    setSelection({ clipIds: [] });
  }, [pushToHistory]);

  // Derived "visible" tracks: Show all core tracks to allow free vertical movement
  const visibleTracks = useMemo<Track[]>(() => {
    // Get track IDs that have clips
    const tracksWithClips = new Set(clips.map(c => c.trackId));

    return [...tracksState]
      .filter((t) => (t as any).visible !== false)
      .filter((t) => {
        // Always show core tracks (video, audio, overlay, text) to enable vertical dragging
        if (t.kind === "video" || t.kind === "audio" || t.kind === "overlay" || t.kind === "text") {
          return true;
        }

        // Keep effects base track always visible so users can create FX blocks immediately.
        if (t.kind === "effects") return true;
        if (t.kind === "transitions") return transitions.length > 0;

        // Show per-element tracks if they have clips
        if (tracksWithClips.has(t.id)) return true;

        // Hide camera and other special tracks by default
        return false;
      })
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [tracksState, clips, transitions.length, fxBlocks.length]);


  const value = useMemo<TimelineState>(() => ({
    assets,
    tracks: visibleTracks,
    clips,
    markers,
    keyframes,
    transitions,
    effectRegions,
    fxBlocks,

    playhead,
    isPlaying,
    loopPlayback,
    loopRegion,
    commentsEnabled,
    comments,
    masterMuted,
    pxPerSec,
    zoom,

    selection,

    // selectors
    hasVideoClips,
    hasAudioClips,
    canAddTrack,

    // asset ops
    addAsset,
    upsertAsset,

    // track ops
    addTrack,
    removeTrack,
    updateTrack,
    reorderTracks,

    // clip ops
    addClip,
    updateClip,
    deleteClip,
    splitClipAt,
    moveClip,
    trimLeft,
    trimRight,
    rippleDelete,
    slipEdit,

    // playback
    setPlayhead,
    setPlaying,
    setLoopPlayback,
    setLoopRegion,
    clearLoopRegion,
    setCommentsEnabled,
    addComment,
    updateComment,
    deleteComment,
    setComments,
    setMasterMuted,
    setPxPerSec,
    setZoom,

    // selection
    selectClips,
    selectAllClips,
    deselectAllClips,
    deleteSelectedClips,

    // keyframes
    addKeyframe,
    updateKeyframe,
    deleteKeyframe,

    // PHASE 1: Volume keyframe helpers
    addVolumeKeyframe,
    getVolumeKeyframes,
    getVolumeAtTime,
    deleteVolumeKeyframes,

    // transitions
    addTransition,
    updateTransition,
    deleteTransition,

    // effect regions
    addEffectRegion,
    updateEffectRegion,
    deleteEffectRegion,
    getEffectAtTime,
    addFxBlock,
    updateFxBlock,
    deleteFxBlock,
    getActiveFxBlocksAtTime,
    selectedFxBlockId,
    setSelectedFxBlockId,

    // undo/redo
    undo,
    redo,
    canUndo,
    canRedo,
    lastEditAt,

    // persistence
    hydrateFromManifest,
    clearTimeline
  }), [
    assets, visibleTracks, clips, markers, keyframes, transitions, effectRegions, fxBlocks,
    playhead, isPlaying, loopPlayback, loopRegion, commentsEnabled, comments, masterMuted, pxPerSec, zoom, selection,
    hasVideoClips, hasAudioClips, canAddTrack,
    addAsset, upsertAsset,
    addTrack, removeTrack, updateTrack, reorderTracks,
    addClip, updateClip, deleteClip,
    splitClipAt, moveClip, trimLeft, trimRight, rippleDelete, slipEdit,
    setPlayhead, setPlaying, setLoopPlayback, setLoopRegion, clearLoopRegion,
    setCommentsEnabled, addComment, updateComment, deleteComment, setComments,
    setMasterMuted, setPxPerSec, setZoom,
    selectClips, selectAllClips, deselectAllClips, deleteSelectedClips,
    addKeyframe, updateKeyframe, deleteKeyframe,
    addVolumeKeyframe, getVolumeKeyframes, getVolumeAtTime, deleteVolumeKeyframes,
    addTransition, updateTransition, deleteTransition,
    addEffectRegion, updateEffectRegion, deleteEffectRegion, getEffectAtTime,
    addFxBlock, updateFxBlock, deleteFxBlock, getActiveFxBlocksAtTime,
    undo, redo, canUndo, canRedo, historyVersion, lastEditAt,
    hydrateFromManifest,
    clearTimeline
  ]);

  // playhead clamp
  useEffect(() => {
    const maxEnd = clips.reduce((m, c) => Math.max(m, c.end), 0);
    const clampEnd = maxEnd > 0 ? maxEnd : 10;
    if (playhead > clampEnd) setPlayhead(clampEnd);
  }, [clips, playhead]);

  // loop region clamp
  useEffect(() => {
    if (!loopRegion) return;
    const maxEnd = clips.reduce((m, c) => Math.max(m, c.end), 0);
    const clampEnd = maxEnd > 0 ? maxEnd : 10;
    const s = clamp(loopRegion.start, 0, clampEnd);
    const e = clamp(loopRegion.end, 0, clampEnd);
    if (e - s < 0.01) {
      setLoopRegionState(null);
      return;
    }
    if (s !== loopRegion.start || e !== loopRegion.end) {
      setLoopRegionState({ start: s, end: e });
    }
  }, [clips, loopRegion]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};
